import { Info, TriangleAlert } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { periodicReleaseCurrentPeriodLabel } from '@wevo/wevo-access/sow-credit-pools';
import {
    formatCredits,
    getPoolGrantedBreakdown,
    isPeriodicReleasePool,
    type PoolBalanceRow,
} from './adminCreditAdapters';
import { DialogFrame } from './creditDialogPrimitives';
import { formatInclusiveDateRange } from './dateDisplay';
import { describePeriodicReleaseScheduleSummary } from './sowAllocationModel';
import { cn } from '../../../lib/utils';
import { clampUsageProgressPercent, formatUsageSharePercent } from './sowUsageProgress';
import { cardTitleClassName } from '../../ui/typography';

const PROGRESS_TRACK_CLASS =
    'h-2 overflow-hidden rounded-full bg-neutral-100 ring-1 ring-inset ring-neutral-400';
const COMMITTED_FILL_CLASS = 'h-full bg-primary-500';
const PENDING_FILL_CLASS = 'h-full usage-reserved-hatch';
const COMMITTED_SWATCH_CLASS = 'bg-primary-500';
const PENDING_SWATCH_CLASS = 'usage-reserved-hatch';

function OverdrawnGlyph({ overdrawn }: { overdrawn: boolean }) {
    return overdrawn ? (
        <TriangleAlert aria-hidden className="mr-1 inline size-4 align-[-2px] text-amber-600" />
    ) : null;
}

function PoolAvailability({
    available,
    granted,
    overdrawn,
}: {
    available: number;
    granted: number;
    overdrawn: boolean;
}) {
    return (
        <p
            className={cn(
                'whitespace-nowrap text-lg font-semibold tabular-nums',
                overdrawn ? 'text-amber-800' : 'text-neutral-950',
            )}
        >
            <OverdrawnGlyph overdrawn={overdrawn} />
            {formatCredits(available)}{' '}
            <span className="text-sm font-medium text-neutral-500">
                available of {formatCredits(granted)}
            </span>
        </p>
    );
}

interface PoolUsageSegmentAmounts {
    usageGranted: number;
    usageCommitted: number;
    usagePending: number;
    available: number;
}

function UsageShareDot({ swatchClassName }: { swatchClassName: string | null }) {
    return (
        <span
            aria-hidden
            className={cn(
                'inline-block size-2.5 shrink-0 rounded-sm align-middle',
                swatchClassName ?? 'border border-neutral-400 bg-transparent',
            )}
        />
    );
}

function PoolUsageBreakdown({
    id,
    amounts,
    align = 'start',
}: {
    id: string;
    amounts: PoolUsageSegmentAmounts;
    align?: 'start' | 'end';
}) {
    const rows = [
        {
            label: 'Spent',
            swatchClassName: COMMITTED_SWATCH_CLASS,
            value: formatUsageSharePercent(amounts.usageCommitted, amounts.usageGranted),
        },
        {
            label: 'Reserved',
            swatchClassName: PENDING_SWATCH_CLASS,
            value: formatUsageSharePercent(amounts.usagePending, amounts.usageGranted),
        },
        {
            label: 'Available',
            swatchClassName: null,
            value: formatUsageSharePercent(amounts.available, amounts.usageGranted),
        },
    ];

    return (
        <div
            id={id}
            role="tooltip"
            data-sow-pool-breakdown
            className={cn(
                'pointer-events-none absolute bottom-full z-20 mb-2 min-w-[11.625rem] rounded-[10px] border border-neutral-200 bg-white px-3 py-2.5 text-xs text-neutral-950 opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100',
                align === 'end' ? 'right-0' : 'left-0',
            )}
        >
            {rows.map((row) => (
                <div
                    key={row.label}
                    className="flex items-center justify-between gap-[18px]"
                    style={{ lineHeight: 1.85 }}
                >
                    <span className="inline-flex items-center gap-2 text-neutral-500">
                        <UsageShareDot swatchClassName={row.swatchClassName} />
                        {row.label}
                    </span>
                    <span className="font-semibold tabular-nums">{row.value}</span>
                </div>
            ))}
        </div>
    );
}

function PoolUsageLegend({ amounts }: { amounts: PoolUsageSegmentAmounts }) {
    return (
        <span
            data-sow-pool-legend
            className="inline-flex flex-wrap items-center gap-2 text-xs text-neutral-500"
        >
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <UsageShareDot swatchClassName={COMMITTED_SWATCH_CLASS} />
                {formatCredits(amounts.usageCommitted)} spent
            </span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <UsageShareDot swatchClassName={PENDING_SWATCH_CLASS} />
                {formatCredits(amounts.usagePending)} reserved
            </span>
        </span>
    );
}

function formatPeriodLabel(
    start: string | null | undefined,
    end: string | null | undefined,
): string | null {
    return formatInclusiveDateRange(start, end);
}

function StackedUsageBar({
    committedPercent,
    pendingPercent,
    usagePercent,
    breakdownId,
    breakdownAmounts,
    ariaLabel,
    className,
    breakdownAlign,
}: {
    committedPercent: number;
    pendingPercent: number;
    usagePercent: number;
    breakdownId?: string;
    breakdownAmounts?: PoolUsageSegmentAmounts;
    ariaLabel?: string;
    className?: string;
    breakdownAlign?: 'start' | 'end';
}) {
    const c = clampUsageProgressPercent(committedPercent);
    const p = clampUsageProgressPercent(pendingPercent);
    const bar = (
        <div
            data-sow-pool-progress
            role="progressbar"
            tabIndex={breakdownAmounts ? 0 : undefined}
            aria-label={ariaLabel}
            aria-describedby={breakdownAmounts ? breakdownId : undefined}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={clampUsageProgressPercent(usagePercent)}
            className={cn(PROGRESS_TRACK_CLASS, !breakdownAmounts && className)}
        >
            <div className="flex h-full w-full min-w-0 gap-px">
                {c > 0 ? (
                    <div className={`${COMMITTED_FILL_CLASS} min-w-0`} style={{ width: `${c}%` }} />
                ) : null}
                {p > 0 ? (
                    <div className={`${PENDING_FILL_CLASS} min-w-0`} style={{ width: `${p}%` }} />
                ) : null}
            </div>
        </div>
    );

    if (!breakdownAmounts || !breakdownId) {
        return bar;
    }

    return (
        <div className={cn('group relative', className)}>
            {bar}
            <PoolUsageBreakdown
                id={breakdownId}
                amounts={breakdownAmounts}
                align={breakdownAlign}
            />
        </div>
    );
}

/** Compact fraction + progress line + percent for agreement rows in tables. */
export function SowTableUsageMetrics({
    used,
    granted,
    usageCommitted,
    usagePending,
    usagePercent,
    committedUsagePercent,
    pendingUsagePercent,
}: {
    used: number;
    granted: number;
    usageCommitted: number;
    usagePending: number;
    usagePercent: number;
    committedUsagePercent: number;
    pendingUsagePercent: number;
}) {
    const breakdownId = useId();
    const overdrawn = used > granted;
    const breakdownAmounts: PoolUsageSegmentAmounts = {
        usageGranted: granted,
        usageCommitted,
        usagePending,
        available: Math.max(granted - used, 0),
    };

    return (
        <div className="inline-flex min-w-[7rem] flex-col items-end gap-1.5">
            <span
                className={cn(
                    'inline-flex items-center gap-1 font-medium tabular-nums',
                    overdrawn ? 'text-amber-800' : 'text-neutral-950',
                )}
            >
                {overdrawn ? (
                    <TriangleAlert aria-hidden className="size-3.5 shrink-0 text-amber-600" />
                ) : null}
                {formatCredits(used)} / {formatCredits(granted)}
            </span>
            <StackedUsageBar
                committedPercent={committedUsagePercent}
                pendingPercent={pendingUsagePercent}
                usagePercent={usagePercent}
                breakdownId={breakdownId}
                breakdownAmounts={breakdownAmounts}
                ariaLabel="Credit usage"
                className="self-stretch"
                breakdownAlign="end"
            />
        </div>
    );
}

function getCurrentPeriodAllocationBreakdown(pool: PoolBalanceRow): {
    releaseAmount: number;
    carryIn: number;
    periodTotal: number;
} {
    const currentPeriod = pool.periodSummaries.find((period) => period.isCurrent);
    const releaseAmount = currentPeriod ? Number(currentPeriod.releaseAmount) : 0;
    const previousPeriod = currentPeriod
        ? pool.periodSummaries.find((period) => period.index === currentPeriod.index - 1)
        : undefined;
    const carryIn = previousPeriod ? Number(previousPeriod.carriedIntoNext ?? 0) : 0;

    return {
        releaseAmount: Number.isFinite(releaseAmount) ? releaseAmount : 0,
        carryIn: Number.isFinite(carryIn) ? carryIn : 0,
        periodTotal: pool.usageGranted,
    };
}

function usageFractionCaption(pool: PoolBalanceRow): string {
    if (pool.usageScope === 'current_period') {
        const periodLabel = periodicReleaseCurrentPeriodLabel(pool.resetPolicy?.cadence);
        if (periodLabel) {
            return `used / ${periodLabel.toLowerCase()}`;
        }
        return 'used / this period';
    }
    return 'used / total';
}

function poolUsageBreakdownAriaLabel(pool: PoolBalanceRow): string {
    if (pool.usageScope === 'current_period') {
        return `Show ${pool.label} current period breakdown`;
    }
    return `Show ${pool.label} total breakdown`;
}

function PoolGrantedBreakdownDialog({
    open,
    pool,
    onClose,
}: {
    open: boolean;
    pool: PoolBalanceRow;
    onClose: () => void;
}) {
    const showsCurrentPeriodUsage = pool.usageScope === 'current_period';
    const periodBreakdown = showsCurrentPeriodUsage
        ? getCurrentPeriodAllocationBreakdown(pool)
        : null;
    const fullContractAmount = pool.contracted;
    const periodDateLabel = showsCurrentPeriodUsage
        ? formatPeriodLabel(pool.currentPeriodStart, pool.currentPeriodEnd)
        : null;

    if (showsCurrentPeriodUsage && periodBreakdown) {
        return (
            <DialogFrame
                open={open}
                title={`${pool.label} current period breakdown`}
                description="How this period's usage limit is composed."
                onClose={onClose}
                panelClassName="max-w-md"
            >
                {periodDateLabel ? (
                    <p className="mb-3 text-sm text-neutral-500">
                        Period (inclusive): {periodDateLabel}
                    </p>
                ) : null}
                <dl className="space-y-3 text-sm">
                    <div className="flex items-baseline justify-between gap-4">
                        <dt className="text-neutral-600">Released this period</dt>
                        <dd className="font-medium tabular-nums text-neutral-950">
                            {formatCredits(periodBreakdown.releaseAmount)}
                        </dd>
                    </div>
                    {periodBreakdown.carryIn > 0 ? (
                        <div className="flex items-baseline justify-between gap-4">
                            <dt className="text-neutral-600">Carried from prior period</dt>
                            <dd className="font-medium tabular-nums text-neutral-950">
                                {formatCredits(periodBreakdown.carryIn)}
                            </dd>
                        </div>
                    ) : null}
                    <div className="flex items-baseline justify-between gap-4 border-t border-neutral-200 pt-3">
                        <dt className="font-medium text-neutral-950">Total for this period</dt>
                        <dd className="font-semibold tabular-nums text-neutral-950">
                            {formatCredits(periodBreakdown.periodTotal)}
                        </dd>
                    </div>
                </dl>
                <p className="mt-4 text-xs text-neutral-500">
                    Lifetime granted balance: {formatCredits(pool.granted)} credits. See release
                    schedule below for contract totals.
                </p>
            </DialogFrame>
        );
    }

    const breakdown = getPoolGrantedBreakdown(pool);

    return (
        <DialogFrame
            open={open}
            title={`${pool.label} total breakdown`}
            description="How the current granted balance is composed."
            onClose={onClose}
            panelClassName="max-w-md"
        >
            <dl className="space-y-3 text-sm">
                <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-neutral-600">Contracted</dt>
                    <dd className="font-medium tabular-nums text-neutral-950">
                        {formatCredits(breakdown.contracted)}
                    </dd>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-neutral-600">Rolled in</dt>
                    <dd className="font-medium tabular-nums text-neutral-950">
                        {formatCredits(breakdown.rolledIn)}
                    </dd>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-neutral-600">Additional grants</dt>
                    <dd className="font-medium tabular-nums text-neutral-950">
                        {formatCredits(breakdown.additionalGrants)}
                    </dd>
                </div>
                <div className="flex items-baseline justify-between gap-4 border-t border-neutral-200 pt-3">
                    <dt className="font-medium text-neutral-950">Total granted</dt>
                    <dd className="font-semibold tabular-nums text-neutral-950">
                        {formatCredits(breakdown.totalGranted)}
                    </dd>
                </div>
            </dl>
            {fullContractAmount > breakdown.contracted ? (
                <p className="mt-4 text-xs text-neutral-500">
                    Full contract amount: {formatCredits(fullContractAmount)} credits.
                </p>
            ) : null}
        </DialogFrame>
    );
}

/** One credit pool summary for agreement detail pages (admin + company read-only). */
export function SowPoolUsageSection({
    pool,
    footer,
}: {
    pool: PoolBalanceRow;
    footer?: ReactNode;
}) {
    const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
    const isPeriodicRelease = isPeriodicReleasePool(pool.resetPolicy);
    const showsCurrentPeriodUsage = pool.usageScope === 'current_period';
    const currentPeriodLabel = showsCurrentPeriodUsage
        ? formatPeriodLabel(pool.currentPeriodStart, pool.currentPeriodEnd)
        : null;
    const releaseScheduleSummary = isPeriodicRelease
        ? describePeriodicReleaseScheduleSummary(pool.resetPolicy)
        : null;
    const currentPeriodBadgeLabel = showsCurrentPeriodUsage
        ? periodicReleaseCurrentPeriodLabel(pool.resetPolicy?.cadence)
        : null;
    const breakdownAmounts: PoolUsageSegmentAmounts = {
        usageGranted: pool.usageGranted,
        usageCommitted: pool.usageCommitted,
        usagePending: pool.usagePending,
        available: pool.usageAvailable,
    };
    const hasRecordedUsage = pool.usageCommitted > 0 || pool.usagePending > 0;
    const breakdownId = `${pool.poolType}-usage-breakdown`;
    const overdrawn = pool.usageAvailable < 0;

    return (
        <>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className={cardTitleClassName}>{pool.label}</h2>
                        {currentPeriodBadgeLabel ? (
                            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                                {currentPeriodBadgeLabel}
                            </span>
                        ) : null}
                    </div>
                    <p className="mt-1 whitespace-nowrap text-2xl font-semibold tabular-nums text-neutral-950">
                        {formatCredits(pool.used)} /{' '}
                        <span className="inline-flex items-center gap-1.5">
                            {formatCredits(pool.usageGranted)}
                            <button
                                type="button"
                                className="-m-1 inline-flex items-center justify-center rounded-full p-1 text-primary-500 hover:bg-primary-500/10"
                                aria-label={poolUsageBreakdownAriaLabel(pool)}
                                onClick={() => setIsBreakdownOpen(true)}
                            >
                                <Info className="size-4 shrink-0" aria-hidden />
                            </button>
                        </span>
                    </p>
                    <p className="text-sm text-neutral-500">{usageFractionCaption(pool)}</p>
                </div>
                <div className="sm:shrink-0 sm:text-right">
                    <PoolAvailability
                        available={pool.usageAvailable}
                        granted={pool.usageGranted}
                        overdrawn={overdrawn}
                    />
                </div>
            </div>
            <StackedUsageBar
                committedPercent={pool.committedUsagePercent}
                pendingPercent={pool.pendingUsagePercent}
                usagePercent={pool.usagePercent}
                breakdownId={breakdownId}
                breakdownAmounts={breakdownAmounts}
                ariaLabel={`${pool.label} usage`}
            />
            {releaseScheduleSummary ? (
                <div className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50/80 px-3 py-2">
                    <p className="text-sm font-medium text-neutral-950">Release schedule</p>
                    <p className="text-sm text-neutral-500">{releaseScheduleSummary}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-600">
                        <span>
                            <span className="text-neutral-500">Contracted:</span>{' '}
                            <span className="font-medium tabular-nums text-neutral-950">
                                {formatCredits(pool.contracted)}
                            </span>
                        </span>
                        <span>
                            <span className="text-neutral-500">Released:</span>{' '}
                            <span className="font-medium tabular-nums text-neutral-950">
                                {formatCredits(pool.released)}
                            </span>
                        </span>
                        {pool.fromRollover > 0 ? (
                            <span>
                                <span className="text-neutral-500">From rollover:</span>{' '}
                                <span className="font-medium tabular-nums text-neutral-950">
                                    {formatCredits(pool.fromRollover)}
                                </span>
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}
            {pool.expiredByPolicy > 0 || currentPeriodLabel ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-500">
                    {pool.expiredByPolicy > 0 ? (
                        <span>Expired by policy: {formatCredits(pool.expiredByPolicy)}</span>
                    ) : null}
                    {currentPeriodLabel ? (
                        <span>Current period (inclusive): {currentPeriodLabel}</span>
                    ) : null}
                </div>
            ) : null}
            {hasRecordedUsage ? (
                <div className="flex flex-wrap items-center justify-end gap-3 text-sm">
                    <PoolUsageLegend amounts={breakdownAmounts} />
                </div>
            ) : null}
            {footer}
            <PoolGrantedBreakdownDialog
                open={isBreakdownOpen}
                pool={pool}
                onClose={() => setIsBreakdownOpen(false)}
            />
        </>
    );
}
